import React from 'react';
import { CheckCircle, CreditCard, FileText, X, Calendar, Users, Euro, Tag } from 'lucide-react';
import { format } from 'date-fns';
import { de } from 'date-fns/locale';
import './BookingSuccessModal.css';

interface BookingSuccessModalProps {
  bookingId: number;
  bookingNumber?: string;
  scheduleDate: string;
  scheduleTime: string;
  tourType: string;
  participantCount: number;
  totalAmount: number;
  voucherCode?: string;
  voucherDiscount?: number;
  // T-020 (02.09.2026): Zahlungsart aus der gespeicherten Buchung statt statischem Anzahlungstext
  isShortTermBooking?: boolean;
  fullPaymentChosen?: boolean;
  onClose: () => void;
  onPayNow: () => void;
  onViewBooking: () => void;
}

const BookingSuccessModal: React.FC<BookingSuccessModalProps> = ({
  bookingId,
  bookingNumber,
  scheduleDate,
  scheduleTime,
  tourType,
  participantCount,
  totalAmount,
  voucherCode,
  voucherDiscount,
  isShortTermBooking,
  fullPaymentChosen,
  onClose,
  onPayNow,
  onViewBooking
}) => {
  const formatDate = (dateStr: string) => {
    try {
      const date = new Date(dateStr);
      return format(date, 'EEEE, d. MMMM yyyy', { locale: de });
    } catch {
      return dateStr;
    }
  };

  const formatTime = (timeStr: string) => {
    if (!timeStr) return '';
    // Handle both "HH:mm" and "HH:mm:ss" formats
    const parts = timeStr.split(':');
    return `${parts[0]}:${parts[1]} Uhr`;
  };

  const formatCurrency = (amount: number) => {
    return new Intl.NumberFormat('de-DE', {
      style: 'currency',
      currency: 'EUR'
    }).format(amount);
  };

  const getTourTypeName = (type: string): string => {
    const names: { [key: string]: string } = {
      'STANDARD': 'Standard Floßfahrt',
      'ALL_IN': 'All-In Floßfahrt',
      'ALL_IN_PREMIUM': 'All-In Premium',
      'EXCLUSIVE': 'Exklusiv Tour',
      'WEDDING': 'Hochzeits-Tour'
    };
    return names[type] || type;
  };

  // Check if short-notice booking (< 8 weeks = 56 days), Datumsregel als Fallback
  const isShortNoticeByDate = (() => {
    try {
      const tripDate = new Date(scheduleDate);
      const today = new Date();
      const daysUntilTrip = Math.floor((tripDate.getTime() - today.getTime()) / (1000 * 60 * 60 * 24));
      return daysUntilTrip < 56;
    } catch {
      return false;
    }
  })();

  // T-020: Backend-Flag (kurzfristig bzw. Komplettzahlung gewaehlt, beides fuehrt zur Kurzfristigen
  // Rechnung) hat Vorrang vor der Datumsregel. Komplettzahlung ohne Kurzfristigkeit wird eigens benannt.
  const isShortNotice = isShortTermBooking === true || isShortNoticeByDate;
  const isFullPaymentByChoice = fullPaymentChosen === true && !isShortNoticeByDate;
  const isFullPayment = isShortNotice || fullPaymentChosen === true;

  // Vollzahlung (kurzfristig oder gewaehlt), sonst 20 Prozent Anzahlung
  const paymentAmount = isFullPayment ? totalAmount : totalAmount * 0.2;
  const paymentLabel = isFullPaymentByChoice
    ? 'Komplettzahlung (gewählt)'
    : isFullPayment
      ? 'Vollzahlung (kurzfristig)'
      : 'Anzahlung (20%)';

  return (
    <div className="booking-success-modal-overlay" onClick={onClose}>
      <div className="booking-success-modal" onClick={e => e.stopPropagation()}>
        <button className="close-button" onClick={onClose} aria-label="Schließen">
          <X size={24} />
        </button>

        <div className="success-header">
          <div className="success-icon">
            <CheckCircle size={64} />
          </div>
          <h2>Buchung erfolgreich!</h2>
          <p className="success-subtitle">
            Ihre Floßfahrt wurde erfolgreich gebucht.
          </p>
        </div>

        <div className="booking-details">
          <h3>Buchungsdetails</h3>

          {bookingNumber && (
            <div className="detail-row">
              <span className="detail-label">
                <Tag size={18} />
                Buchungsnummer
              </span>
              <span className="detail-value booking-number">{bookingNumber}</span>
            </div>
          )}

          <div className="detail-row">
            <span className="detail-label">
              <Calendar size={18} />
              Datum & Uhrzeit
            </span>
            <span className="detail-value">
              {formatDate(scheduleDate)}, {formatTime(scheduleTime)}
            </span>
          </div>

          <div className="detail-row">
            <span className="detail-label">
              <FileText size={18} />
              Tour-Typ
            </span>
            <span className="detail-value">{getTourTypeName(tourType)}</span>
          </div>

          {participantCount > 0 && (
            <div className="detail-row">
              <span className="detail-label">
                <Users size={18} />
                Teilnehmer
              </span>
              <span className="detail-value">{participantCount} Personen</span>
            </div>
          )}

          {totalAmount > 0 && (
            <>
              {/* T-029 (02.09.2026): Vergleich statt voucherDiscount && ..., sonst rendert React bei 0 eine nackte "0" */}
              {voucherDiscount != null && voucherDiscount > 0 && (
                <div className="detail-row voucher">
                  <span className="detail-label">
                    <Tag size={18} />
                    Gutschein ({voucherCode})
                  </span>
                  <span className="detail-value voucher-discount">-{formatCurrency(voucherDiscount)}</span>
                </div>
              )}
              <div className="detail-row total">
                <span className="detail-label">
                  <Euro size={18} />
                  Gesamtpreis
                </span>
                <span className="detail-value">{formatCurrency(totalAmount)}</span>
              </div>
              <div className="detail-row deposit">
                <span className="detail-label">
                  <CreditCard size={18} />
                  {paymentLabel}
                </span>
                <span className="detail-value">{formatCurrency(paymentAmount)}</span>
              </div>
            </>
          )}
        </div>

        <div className="info-box">
          <p>
            <strong>Nächste Schritte:</strong>{' '}
            {isFullPaymentByChoice ? (
              <>
                Sie haben die Komplettzahlung gewählt: Bitte zahlen Sie den
                <strong> Gesamtbetrag von {formatCurrency(paymentAmount)}</strong>, um Ihre Buchung zu bestätigen.
              </>
            ) : isFullPayment ? (
              <>
                Da Ihre Buchung kurzfristig erfolgt (weniger als 8 Wochen vor der Fahrt),
                ist die <strong>Vollzahlung von {formatCurrency(paymentAmount)}</strong> fällig.
              </>
            ) : (
              <>
                Bitte zahlen Sie die Anzahlung von{' '}
                <strong>{formatCurrency(paymentAmount)}</strong>, um Ihre Buchung zu bestätigen.
                Die Restzahlung ist 25 Tage vor der Fahrt fällig.
              </>
            )}
            {' '}Sie erhalten eine Bestätigungs-E-Mail mit allen Details.
          </p>
        </div>

        <div className="action-buttons">
          <button className="btn-primary" onClick={onPayNow}>
            <CreditCard size={20} />
            Jetzt bezahlen
          </button>
          <button className="btn-secondary" onClick={onViewBooking}>
            <FileText size={20} />
            Buchungsdetails ansehen
          </button>
        </div>

        <button className="btn-text" onClick={onClose}>
          Später bezahlen
        </button>
      </div>
    </div>
  );
};

export default BookingSuccessModal;
